/**
 * Legale **Schläge** (CAPTURE).
 *
 * Grundprinzip für das ganze Spiel (§21): **schlagen heißt, auf das Feld des Gegners zu
 * ziehen.** Keine Figur schlägt aus der Entfernung und bleibt stehen — auch der Wächter
 * nicht, der auf Distanz 2 schlägt und dann zwei Felder weiter steht (Variante A, vom User
 * am 2026-10-02 entschieden). Die einzige Figur, die aus der Entfernung wirkt, ist der
 * Stoßer, und sein PUSH ist kein CAPTURE (`legal-pushes.ts`).
 *
 * Deshalb benutzt diese Datei dieselben Pfade wie die Bewegung (`path-walk.ts`), nur mit
 * der Schlagreichweite aus `captureRange` — die den Marker `'sameAsMovement'` für Läufer
 * und Springer bereits aufgelöst hat.
 *
 * **Geschlagen wird nur ein Gegner** — eine Figur einer anderen **Seite**, nie die eines
 * Teamkollegen (Nr. 54, Zyklus `R15`). Ein Teamkollege versperrt den Weg wie eine eigene Figur.
 *
 * Zwei Figuren schlagen **nicht** auf diesem Weg:
 *
 * - Der **Stoßer** hat keine Schlagreichweite (§18).
 * - Der **Fähnrich** schlägt keine normalen Figuren. Seine einzige Ausnahme ist der
 *   gegnerische Fähnrich auf dem Zielfeld (§14) — dorthin zieht er mit seiner
 *   **Bewegungsreichweite**, denn §14 beschreibt es als Ziehen.
 */
import { mayCaptureOn } from './goal-cell.js';
import { hexKey } from './hex.js';
import { canJump, captureRange, movementRange } from './pieces.js';
import { walkPaths } from './path-walk.js';
import { isGoalCell } from './goal-cell.js';
import { requirePiece } from './board-state.js';
import { areOpponents } from './rule-context.js';
/**
 * Der Sonderfall Fähnrich: ausschließlich ein gegnerischer Fähnrich auf dem Zielfeld,
 * erreichbar auf einem normalen Bewegungspfad.
 */
function flagBearerCaptures(context, piece) {
    const targets = new Map();
    walkPaths(context, piece, movementRange(context.config, piece.type), canJump(context.config, piece.type), ({ cell, occupant }) => {
        if (!isGoalCell(cell))
            return;
        if (occupant === undefined || !areOpponents(context, piece.owner, occupant.owner))
            return;
        if (occupant.type !== 'flagBearer')
            return;
        targets.set(hexKey(cell), cell);
    });
    return [...targets.values()];
}
/**
 * Alle Felder, auf denen diese Figur in diesem Zug schlagen darf. Das Zielfeld ist
 * gleichzeitig das Feld, auf dem die Figur danach steht.
 */
export function getLegalCaptures(context, pieceId) {
    const piece = requirePiece(context.state, pieceId);
    if (piece.type === 'flagBearer')
        return flagBearerCaptures(context, piece);
    const range = captureRange(context.config, piece.type);
    if (range === undefined)
        return [];
    const targets = new Map();
    walkPaths(context, piece, range, canJump(context.config, piece.type), ({ cell, occupant }) => {
        // Nur Gegner, nie ein Teamkollege (Nr. 54, `R15`; bis dahin stand hier der Besitzer).
        if (occupant === undefined || !areOpponents(context, piece.owner, occupant.owner))
            return;
        // Ein Fähnrich auf dem Zielfeld ist gegen alles außer einem Fähnrich geschützt (§21).
        if (!mayCaptureOn(cell, piece.type))
            return;
        targets.set(hexKey(cell), cell);
    });
    return [...targets.values()];
}
//# sourceMappingURL=legal-captures.js.map